import { useEffect, useRef } from "react";

import { ConsultationPreview } from "components/ConsultationPreview/ConsultationPreview";
import { SearchInput } from "components/ui";
import { Button } from "components/ui/Button";
import { DataTablePagination } from "components/ui/DataTable/components/DataTablePagination/DataTablePagination";
import { useAntenatalConsultations } from "features/pregnancy/hooks/useAntenatalConsultations";
import { useConsultationViewState } from "features/pregnancy/hooks/useConsultationViewState";
import { usePaginatedConsultations } from "features/pregnancy/hooks/usePaginatedConsultations";
import { formatDoctorName } from "features/pregnancy/utils/formatDoctorName";

import { PregnancyLoader } from "../../PregnancyLoader/PregnancyLoader";
import { PregnancyAnteNatalConsultationContent } from "../PregnancyAnteNatalConsultation/PregnancyAnteNatalConsultationContent";
import { PregnancyAnteNatalConsultationForm } from "../PregnancyAnteNatalConsultationForm/PregnancyAnteNatalConsultationForm";
import { PregnancyNoAnteNatalConsultationsFound } from "../PregnancyNoAnteNatalConsultationsFound/PregnancyNoAnteNatalConsultationsFound";
import style from "./PregnancyAnteNatalView.module.css";

export const PregnancyAnteNatalView = ({
  pregnancy,
  tenantVal,
  patientId,
  editConsultationId,
}) => {
  const {
    activeConsultation,
    isFormOpen,
    page,
    setPage,
    rowsPerPage,
    search,
    openAdd,
    openEdit,
    closeForm,
    handleSearchChange,
    handleRowsChange,
  } = useConsultationViewState();

  const { isLoading, data: anteNatalConsultations } = useAntenatalConsultations(
    pregnancy.id
  );

  // Deep-link from the Messages log "Send correction" → open the matching
  // consultation in edit mode (correction). Runs once per requested id.
  const appliedEditRef = useRef(null);

  useEffect(() => {
    if (!editConsultationId || !anteNatalConsultations?.length) return;
    if (appliedEditRef.current === editConsultationId) return;

    const target = anteNatalConsultations.find(
      (consultation) => consultation.id === editConsultationId
    );

    if (target) {
      appliedEditRef.current = editConsultationId;
      openEdit(target);
    }
  }, [editConsultationId, anteNatalConsultations, openEdit]);

  const { paginated, total, totalPages, start, end } =
    usePaginatedConsultations({
      consultations: anteNatalConsultations,
      searchTerm: search,
      searchFields: [(c) => c.consultationDate, (c) => c.impression],
      page,
      rowsPerPage,
    });

  if (isLoading) {
    return (
      <PregnancyLoader>Loading ante-natal consultations...</PregnancyLoader>
    );
  }

  if (isFormOpen) {
    return (
      <div className={style.pregnancyAnteNatalView}>
        <PregnancyAnteNatalConsultationForm
          closeView={closeForm}
          tenantVal={tenantVal}
          pregnancyId={pregnancy.id}
          patientId={patientId}
          anteNatalConsultation={activeConsultation}
          pregnancy={pregnancy}
        />
      </div>
    );
  }

  /*
   * A completed pregnancy stops offering new antenatal visits (KIN-1942). Deliberately only this
   * action: everything already recorded stays readable and the message log stays reachable,
   * because the record of a pregnancy that ended is exactly what someone comes back to look at.
   * What makes no sense is writing a new antenatal visit against it — and the send would be
   * refused anyway, later and less clearly.
   */
  const isPregnancyCompleted = !!pregnancy?.completed;

  if (anteNatalConsultations?.length < 1) {
    return (
      <PregnancyNoAnteNatalConsultationsFound
        onStartConsultationButtonClick={openAdd}
        canStartConsultation={!isPregnancyCompleted}
      />
    );
  }

  return (
    <div className={style.pregnancyAnteNatalView}>
      <div className={style.viewHeader}>
        <h1 className={style.pregnancyAnteNatalViewTitle}>
          Ante-natal consultations
        </h1>
        {!isPregnancyCompleted && (
          <Button onClick={openAdd}>Start consultation</Button>
        )}
      </div>
      <div className={style.table}>
        <div className={style.tableTopBarWrapper}>
          <SearchInput
            value={search}
            onChange={handleSearchChange}
            placeholder="Search consultations"
          />
        </div>
        <div className={style.consultationPreviewWrapper}>
          {paginated.map((consultation) => {
            const bp =
              consultation.bloodPressureSystolic &&
              consultation.bloodPressureDiastolic
                ? `${consultation.bloodPressureSystolic}/${consultation.bloodPressureDiastolic}`
                : consultation.bloodPressure || null;
            const fundalHeight =
              consultation.fundalHeightCm ?? consultation.symphysis;
            const doctorName =
              formatDoctorName(consultation.conductedByStaffMember) ||
              formatDoctorName(consultation.pregnancy?.conductedByStaffMember);
            const parts = [];
            if (doctorName && doctorName !== "-")
              parts.push(`Dr. ${doctorName}`);
            if (consultation.gestationWeeks != null) {
              const days =
                consultation.gestationDays != null
                  ? `+${consultation.gestationDays}d`
                  : "";
              parts.push(`Gestation: ${consultation.gestationWeeks}w${days}`);
            }
            if (bp) parts.push(`BP: ${bp}`);
            if (consultation.maternalWeightKg)
              parts.push(`Weight: ${consultation.maternalWeightKg} kg`);
            if (fundalHeight) parts.push(`FH: ${fundalHeight} cm`);
            if (consultation.haemoglobin)
              parts.push(`Hb: ${consultation.haemoglobin} g/dL`);
            if (consultation.bmi) parts.push(`BMI: ${consultation.bmi}`);
            if (consultation.ankleOedema)
              parts.push(`Ankle oedema: ${consultation.ankleOedema}`);
            if (consultation.uterineContractions)
              parts.push(`Contractions: ${consultation.uterineContractions}`);
            if (consultation.dateOfNextVisitWeeks)
              parts.push(`Next visit: ${consultation.dateOfNextVisitWeeks}w`);
            if (consultation.impression)
              parts.push(`Impression: ${consultation.impression}`);
            if (consultation.plan) parts.push(`Plan: ${consultation.plan}`);
            const previewText =
              parts.length > 0
                ? parts.join("  ·  ")
                : "Ante-natal consultation";

            return (
              <ConsultationPreview
                key={consultation.id}
                consultation={{
                  ...consultation,
                  date: consultation.consultationDate
                    ? new Date(consultation.consultationDate)
                    : new Date(),
                  title: "Ante-natal consultation",
                }}
                previewText={previewText}
                onEditConsultation={() => openEdit(consultation)}
              >
                <PregnancyAnteNatalConsultationContent
                  consultation={consultation}
                />
              </ConsultationPreview>
            );
          })}
        </div>
        <DataTablePagination
          start={start}
          end={end}
          total={total}
          page={page}
          totalPages={totalPages}
          onPageChange={(p) => setPage(p)}
          rowsOptions={[10, 20, 50]}
          rowsPerPage={rowsPerPage}
          onRowsChange={handleRowsChange}
        />
      </div>
    </div>
  );
};
